import { Activity, Calendar, MessageSquare, Sparkles } from 'lucide-react';
import type { ReviewerScoreEdit } from '../../types';

export interface ActivityEvent {
    id: string;
    kind: 'stage_change' | 'message' | 'note' | 'system' | 'override' | 'email';
    text: string;
    at: string;
    by?: string;
}

export interface ActivityTabProps {
    events?: ActivityEvent[];
    overrides?: ReviewerScoreEdit[];
    /** Optional map of componentId → friendly label so override events read as
     * "Engineering depth" instead of a raw mongoid. */
    componentLabels?: Record<string, string>;
}

const ICONS: Record<ActivityEvent['kind'], React.ComponentType<{ size?: number; strokeWidth?: number }>> = {
    stage_change: Calendar,
    message: MessageSquare,
    note: MessageSquare,
    system: Activity,
    override: Sparkles,
    email: MessageSquare,
};

function formatRelative(iso: string): string {
    const ms = Date.now() - new Date(iso).getTime();
    if (Number.isNaN(ms)) return iso;
    const sec = Math.round(ms / 1000);
    if (sec < 60) return 'just now';
    const min = Math.round(sec / 60);
    if (min < 60) return `${min}m ago`;
    const hr = Math.round(min / 60);
    if (hr < 24) return `${hr}h ago`;
    const days = Math.round(hr / 24);
    return `${days}d ago`;
}

export function ActivityTab({ events = [], overrides = [], componentLabels = {} }: ActivityTabProps) {
    const overrideEvents: ActivityEvent[] = overrides.map((edit) => {
        const label = componentLabels[edit.componentId] || 'a criterion';
        const reviewer = edit.reviewerName ?? 'A reviewer';
        return {
            id: `override-${edit.componentId}-${edit.editedAt}`,
            kind: 'override' as const,
            text: `${reviewer} adjusted ${label} from ${edit.originalScore} to ${edit.editedScore}`,
            at: edit.editedAt,
        };
    });

    const merged = [...overrideEvents, ...events].sort(
        (a, b) => new Date(b.at).getTime() - new Date(a.at).getTime(),
    );

    if (merged.length === 0) {
        return (
            <div className="px-5 py-12 text-center text-[14px]" style={{ color: 'var(--text-tertiary)' }}>
                <Activity
                    size={22}
                    strokeWidth={1.5}
                    style={{ display: 'inline', marginBottom: 8, color: 'var(--text-quaternary)' }}
                />
                <div>No activity yet.</div>
            </div>
        );
    }

    return (
        <div className="px-5 py-4 flex flex-col gap-3">
            {merged.map((ev) => {
                const Icon = ICONS[ev.kind];
                const isOverride = ev.kind === 'override';
                return (
                    <div key={ev.id} className="flex items-start gap-3">
                        <div
                            className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 mt-0.5"
                            style={{
                                background: isOverride ? 'var(--accent-subtle)' : 'var(--surface-secondary)',
                                border: '1px solid var(--border-default)',
                                color: isOverride ? 'var(--accent)' : 'var(--text-tertiary)',
                            }}
                        >
                            <Icon size={14} strokeWidth={1.75} />
                        </div>
                        <div className="flex-1 min-w-0 pt-1">
                            <div className="flex items-baseline justify-between gap-3">
                                <span className="text-[14px] leading-snug" style={{ color: 'var(--text-primary)' }}>
                                    {ev.text}
                                </span>
                                <span
                                    className="text-[12px] tabular-nums shrink-0"
                                    style={{ color: 'var(--text-quaternary)' }}
                                >
                                    {formatRelative(ev.at)}
                                </span>
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
